import React, { useState, useEffect } from 'react';
import JsonViewer from './components/JsonViewer';
import XmlViewer from './components/XmlViewer';
import ComparisonTool from './components/ComparisonTool';
import FileConverter from './components/FileConverter';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Contact from './components/Contact';
import AdSense from './components/AdSense';
import './styles/App.css';

// Set to true when you integrate Google AdSense
const SHOW_ADS = false;

function App() {
  const [activeTab, setActiveTab] = useState('json');
  const [currentPage, setCurrentPage] = useState(null);

  // ✅ Sync app state with URL hash
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');

      if (['privacy', 'terms', 'contact'].includes(hash)) {
        setCurrentPage(hash);
        setActiveTab(null);
      } else {
        setCurrentPage(null);
      }
    };

    handleHashChange(); // Run on initial load
    window.addEventListener('hashchange', handleHashChange);

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // ✅ Tool tab navigation (clears hash)
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  // ✅ Legal page navigation
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setActiveTab(null);
    window.location.hash = page;
    window.scrollTo(0, 0);
  };

  return (
    <div className="App">
      <header className="App-header">
        {/* Header Ad - Responsive Banner */}
        {SHOW_ADS && (
          <div className="header-ad-container" style={{ marginBottom: '1rem' }}>
            <AdSense
              adFormat="auto"
              style={{ width: '100%', maxWidth: '728px', height: '90px', margin: '0 auto' }}
              className="ad-header"
            />
          </div>
        )}
        
        <div className="header-content">
          <div className="header-left">
            <h1>Easy Coding Tools</h1>
            <p className="header-subtitle">
              Professional JSON, XML Tools & File Converters for Developers
            </p>
          </div>
        </div>

        <nav className="tabs">
          <button
            className={activeTab === 'json' ? 'active' : ''}
            onClick={() => handleTabChange('json')}
          >
            📄 JSON Viewer
          </button>

          <button
            className={activeTab === 'xml' ? 'active' : ''}
            onClick={() => handleTabChange('xml')}
          >
            📋 XML Viewer
          </button>

          <button
            className={activeTab === 'compare' ? 'active' : ''}
            onClick={() => handleTabChange('compare')}
          >
            ⚖️ Comparison Tool
          </button>

          <button
            className={activeTab === 'converter' ? 'active' : ''}
            onClick={() => handleTabChange('converter')}
          >
            🔄 File Converter
          </button>
        </nav>
      </header>

      <div className="main-wrapper">
        <main className="App-main">
          {currentPage === 'privacy' && <PrivacyPolicy />}
          {currentPage === 'terms' && <TermsOfService />}
          {currentPage === 'contact' && <Contact />}

          {!currentPage && activeTab === 'json' && <JsonViewer />}
          {!currentPage && activeTab === 'xml' && <XmlViewer />}
          {!currentPage && activeTab === 'compare' && <ComparisonTool />}
          {!currentPage && activeTab === 'converter' && <FileConverter />}
        </main>

        <aside className="sidebar">
          {/* Sidebar Ad 1 - Medium Rectangle */}
          {SHOW_ADS && (
            <div className="sidebar-ad-container" style={{ marginBottom: '1.5rem' }}>
              <AdSense
                adFormat="rectangle"
                style={{ width: '300px', height: '250px', margin: '0 auto' }}
                className="ad-sidebar"
              />
            </div>
          )}
          
          <div className="sidebar-widget">
            <h3>Quick Tips</h3>
            <ul>
              <li>Validate JSON/XML before comparing</li>
              <li>Use formatted output for readability</li>
              <li>Compare large files side-by-side</li>
              <li>Convert files instantly</li>
            </ul>
          </div>
          
          {/* Sidebar Ad 2 - Medium Rectangle */}
          {SHOW_ADS && (
            <div className="sidebar-ad-container" style={{ marginTop: '1.5rem' }}>
              <AdSense
                adFormat="rectangle"
                style={{ width: '300px', height: '250px', margin: '0 auto' }}
                className="ad-sidebar"
              />
            </div>
          )}
        </aside>
      </div>

      <footer className="App-footer">
        <div className="footer-content">
          <p>&copy; 2026 Easy Coding Tools. All rights reserved.</p>

          <div className="footer-links">
            <a
              href="#privacy"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange('privacy');
              }}
            >
              Privacy Policy
            </a>

            <a
              href="#terms"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange('terms');
              }}
            >
              Terms of Service
            </a>

            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange('contact');
              }}
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
